import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { from, of, throwError, timer } from "rxjs";
import { catchError, retryWhen, switchMap, take } from "rxjs/operators";
import { CompanyRoleResolver } from "./company/shared/service/company-user-permissions.service";
import { InstallComponent } from "./pages/install/install.component";
import { NotFoundComponent } from "./pages/not-found/not-found.component";
import { RateLimitReachedComponent } from "./pages/rate-limit-reached/rate-limit-reached.component";
import { TestLandingPageComponent } from "./pages/test-landing-page/test-landing-page.component";
import { SelectivePreloadingStrategy } from "./shared/utils/preload-strategies";

// Helper function to create retry-enabled route loaders
const createRetryLoader = (importFn: () => Promise<any>, moduleName: string) => {
	return () =>
		from(importFn()).pipe(
			retryWhen((errors) =>
				errors.pipe(
					switchMap((error, index) => {
						// Max 3 retries
						if (index >= 3) {
							console.error(
								`Dynamic import failed after 3 retries for ${moduleName}:`,
								error,
							);
							return throwError(() => error);
						}

						// Exponential backoff: 1s, 2s, 4s
						const delay = 1000 * Math.pow(2, index);
						console.warn(
							`Dynamic import failed, retrying in ${delay}ms (attempt ${index + 1}/3) for ${moduleName}:`,
							error,
						);

						return timer(delay);
					}),
					take(3),
				),
			),
			switchMap((module) => of(module[moduleName])),
			catchError((error) => {
				console.error(
					`Dynamic import failed permanently for ${moduleName}:`,
					error,
				);
				return throwError(() => error);
			}),
		);
};

const routes: Routes = [
	{ path: "rate-limit", component: RateLimitReachedComponent },
	{ path: "test-landing", component: TestLandingPageComponent },
	{ path: "install", component: InstallComponent },
	{
		path: "portal",
		loadChildren: createRetryLoader(
			() => import("./portal/inductee/portal-inductee.module"),
			"PortalInducteeModule",
		),
		data: { preload: "medium" },
	},
	{
		path: "incident-reporting",
		loadChildren: createRetryLoader(
			() => import("./incident-reporting/incident-reporting.module"),
			"IncidentReportingModule",
		),
		data: { preload: "low" },
	},
	{
		path: "sign-in-kiosk",
		loadChildren: createRetryLoader(
			() => import("./sign-in-kiosk/sign-in-kiosk.module"),
			"SignInKioskModule",
		),
		data: { preload: "medium" },
	},
	{
		path: "company-web-portal",
		loadChildren: createRetryLoader(
			() => import("./company-web-portal/company-web-portal.module"),
			"CompanyWebPortalModule",
		),
		data: { preload: "medium" },
	},
	{
		path: "contractor",
		loadChildren: createRetryLoader(
			() => import("./portal/contractor/portal-contractor.module"),
			"PortalContractorModule",
		),
		data: { preload: "low" },
	},
	{
		path: "sign-in",
		loadChildren: createRetryLoader(
			() => import("./portal/sign-in/portal-sign-in.module"),
			"PortalSignInModule",
		),
		data: { preload: "medium" },
	},
	{
		path: "website",
		loadChildren: createRetryLoader(
			() => import("./website/website.module"),
			"WebsiteModule",
		),
		data: { preload: "high" },
	},
	{
		path: "invites",
		loadChildren: createRetryLoader(
			() => import("./invite/invite.module"),
			"InviteModule",
		),
		data: { preload: "medium" },
	},
	{
		path: "attempt",
		loadChildren: createRetryLoader(
			() => import("./attempt/attempt.module"),
			"AttemptModule",
		),
		data: { preload: "high" },
	},
	{
		path: "documents",
		loadChildren: createRetryLoader(
			() => import("./documents/documents.module"),
			"DocumentsModule",
		),
		data: { preload: "medium" },
	},
	{
		path: "referrer",
		loadChildren: createRetryLoader(
			() => import("./referrer/referrer.module"),
			"ReferrerModule",
		),
		data: { preload: "low" },
	},
	{
		path: "induction-preview",
		loadChildren: createRetryLoader(
			() => import("./induction-preview/induction-preview.module"),
			"InductionPreviewModule",
		),
		data: { preload: "low" },
	},
	{
		path: "",
		children: [
			{
				path: "",
				pathMatch: "full",
				redirectTo: "auth",
			},
			{
				path: "company",
				loadChildren: createRetryLoader(
					() => import("./company/company.module"),
					"CompanyModule",
				),
				resolve: {
					permissions: CompanyRoleResolver,
				},
				data: { preload: "high" },
			},
			{
				path: "inductee",
				loadChildren: createRetryLoader(
					() => import("./inductee/inductee.module"),
					"InducteeModule",
				),
				data: { preload: "medium" },
			},
			{
				path: "auth",
				loadChildren: createRetryLoader(
					() => import("./auth/auth.module"),
					"AuthModule",
				),
				data: { preload: "high" },
			},
			{
				path: "oauth",
				loadChildren: createRetryLoader(
					() => import("./oauth/oauth.module"),
					"OauthModule",
				),
				data: { preload: "low" },
			},

			{ path: "**", component: NotFoundComponent },
		],
	},
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			useHash: true,
			paramsInheritanceStrategy: "always",
			preloadingStrategy: SelectivePreloadingStrategy,
		}),
	],
	exports: [RouterModule],
})
export class AppRoutingModule {}
